import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { AppPerfilSelectorComponent } from "../login/app-perfil-selector/app-perfil-selector.component";
import { AppUserSelectorComponent } from "../login/app-user-selector/app-user-selector.component";
import { AuthenticationService } from "../services/authentication.service";
import { KeycloakAuthService } from "../services/keycloak-auth.service";
import storageX from "../storageCore/storageX";
import {
  STORAGE_KEY_CURRENT_USER,
  STORAGE_KEY_ID_MANAGER,
  STORAGE_KEY_ID_PERFIL,
  STORAGE_KEY_ID_SUPERVISOR,
} from "../storageCore/constStorageKeys";

@Injectable({ providedIn: "root" })
export class AuthGuard implements CanActivate {
  private profileSelectionPromise: Promise<boolean> | null = null;
  private userTypeSelectionPromise: Promise<void> | null = null;

  constructor(
    private keycloakAuthService: KeycloakAuthService,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog
  ) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Promise<boolean> {
    if (!this.keycloakAuthService.isAuthenticated()) {
      return false;
    }

    if (this.hasSelectedProfile()) {
      return true;
    }

    let currentUser = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get();
    if (!currentUser) {
      try {
        currentUser = await this.authenticationService.syncUserFromKeycloak();
      } catch (error) {
        console.error("[AUTH_GUARD] falha ao sincronizar usuario", error);
        return false;
      }
    }

    if (this.hasSelectedProfile()) {
      return true;
    }

    if (!currentUser || !Array.isArray(currentUser.perfis)) {
      console.error("[AUTH_GUARD] usuario sem perfis para selecao");
      return false;
    }

    return this.ensureProfileSelection();
  }

  private hasSelectedProfile(): boolean {
    const selectedProfileId = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    return selectedProfileId != null && selectedProfileId !== "";
  }

  private ensureProfileSelection(): Promise<boolean> {
    if (this.profileSelectionPromise) {
      return this.profileSelectionPromise;
    }

    this.profileSelectionPromise = new Promise<boolean>(async (resolve) => {
      try {
        await this.ensureUserTypeSelection();
      } catch (error) {
        console.error("[AUTH_GUARD] falha ao selecionar tipo de usuario", error);
      }

      this.dialog
        .open(AppPerfilSelectorComponent, {
          disableClose: true,
          panelClass: "custom-dialog",
        })
        .afterClosed()
        .subscribe(() => {
          const hasProfile = this.hasSelectedProfile();
          resolve(hasProfile);
          this.profileSelectionPromise = null;
        });
    });

    return this.profileSelectionPromise;
  }

  private ensureUserTypeSelection(): Promise<void> {
    if (!this.shouldSelectUserType()) {
      return Promise.resolve();
    }

    if (this.userTypeSelectionPromise) {
      return this.userTypeSelectionPromise;
    }

    this.userTypeSelectionPromise = new Promise<void>((resolve) => {
      this.dialog
        .open(AppUserSelectorComponent, {
          disableClose: true,
          panelClass: "custom-dialog",
        })
        .afterClosed()
        .subscribe(() => {
          this.userTypeSelectionPromise = null;
          resolve();
        });
    });

    return this.userTypeSelectionPromise;
  }

  private shouldSelectUserType(): boolean {
    const managerId = storageX.whereKeyIs(STORAGE_KEY_ID_MANAGER).get();
    const supervisorId = storageX.whereKeyIs(STORAGE_KEY_ID_SUPERVISOR).get();
    return (
      managerId != null &&
      managerId !== "" &&
      supervisorId != null &&
      supervisorId !== ""
    );
  }
}
